package top.wcpe.allincore.ui.runtime.render.layer

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.Slot
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.FeedbackKind
import top.wcpe.allincore.ui.runtime.render.RenderContext
import top.wcpe.allincore.ui.runtime.render.Renderer
import top.wcpe.allincore.ui.runtime.render.RippleAnimator
import top.wcpe.allincore.ui.runtime.render.SlotFeedbackView
import top.wcpe.allincore.ui.runtime.render.SlotItemView
import top.wcpe.allincore.ui.runtime.render.StaticItemNbt
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.render.model.RenderBatch
import top.wcpe.allincore.ui.runtime.render.model.RenderCommand
import top.wcpe.allincore.ui.runtime.render.model.RenderCommandType
import top.wcpe.allincore.ui.runtime.render.model.RenderLayer
import top.wcpe.allincore.ui.runtime.render.model.StyledFill
import top.wcpe.allincore.ui.runtime.style.CornerRadii
import top.wcpe.allincore.ui.runtime.theme.ControlTheme
import top.wcpe.allincore.ui.runtime.theme.StateLayer
import top.wcpe.allincore.ui.runtime.theme.ThemeAccess
import kotlin.math.abs
import kotlin.math.sin

/**
 * 槽位叠加层渲染器：绘制状态叠加、选中轮廓、反馈动画、涟漪特效。
 *
 * 与 [SlotBackgroundRenderer]、[SlotItemRenderer] 共同组成槽位分层渲染，对应 [RenderLayer.OVERLAY] 层。
 */
@Suppress("MagicNumber", "TooManyFunctions")
class SlotOverlayRenderer : Renderer {

    override fun layer(): RenderLayer = RenderLayer.OVERLAY

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        render(element, box, bridge, RenderContext.NONE)
    }

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge, context: RenderContext) {
        val slot = element as Slot
        val theme = ThemeAccess.current()
        val cornerRadii = slot.cornerRadii() ?: CornerRadii.ZERO

        // 解析物品和预览数据
        var item: SlotItemView? = null
        var preview: SlotItemView? = null
        val staticItem = slot.staticItem()
        if (staticItem != null) {
            val itemId = staticItem.itemId()
            if (!itemId.isNullOrEmpty()) {
                item = SlotItemView(itemId, staticItem.count(), StaticItemNbt.snbtOfCached(staticItem))
            }
        } else {
            item = context.slotItem(slot.groupId(), slot.key())
            preview = context.quickCraftPreview(slot.groupId(), slot.key())
        }

        // 状态叠加层（quickCraft 预览期间不画 hover 高亮，对齐 vanilla 行为）
        drawStateOverlays(slot, box, bridge, cornerRadii, theme, preview != null)
        // 选中轮廓
        drawSelectedOverlay(slot, box, bridge, cornerRadii, theme)
        // 反馈动画
        context.slotFeedback(slot.groupId(), slot.key())?.let { feedback ->
            drawFeedback(slot, box, bridge, feedback, item, theme)
        }
        // ripple
        applyRippleIfActive(slot, box, bridge)
    }

    // ========== 状态叠加层 ==========

    private fun drawStateOverlays(
        slot: Slot,
        box: Box,
        bridge: UiRenderBridge,
        cornerRadii: CornerRadii,
        theme: ControlTheme,
        suppressHover: Boolean,
    ) {
        val overlay = resolveTopStateOverlay(slot, theme, suppressHover) ?: return
        drawStateRect(slot, box, bridge, overlay.color, overlay.opacity, cornerRadii)
    }

    /** 按优先级（pressed > focus > disabled > hovered > warn）取最高状态叠加；无可见叠加返回 null。 */
    @Suppress("CyclomaticComplexMethod") // 五态优先级顺序裁决，分支多但线性清晰
    private fun resolveTopStateOverlay(slot: Slot, theme: ControlTheme, suppressHover: Boolean): StateOverlay? {
        var priority = -1
        var color = 0
        var opacity = 1f
        if (slot.pressed() && priority < STATE_PRIORITY_PRESSED) {
            color = slot.pressedOverlayColor() ?: 0x80FFFFFF.toInt()
            opacity = slot.pressedOverlayOpacity() ?: theme.slotPressedOpacity
            priority = STATE_PRIORITY_PRESSED
        }
        if (slot.focused() && priority < STATE_PRIORITY_FOCUS) {
            color = slot.focusOverlayColor() ?: theme.slotFocusOverlayColor
            opacity = theme.slotFocusOpacity
            priority = STATE_PRIORITY_FOCUS
        }
        if (!slot.enabled() && priority < STATE_PRIORITY_DISABLED) {
            color = slot.disabledOverlayColor() ?: theme.slotDisabledOverlayColor
            opacity = slot.disabledOpacity() ?: theme.slotDisabledOpacity
            priority = STATE_PRIORITY_DISABLED
        }
        if (!suppressHover && slot.hovered() && priority < STATE_PRIORITY_HOVER) {
            color = slot.hoverOverlayColor() ?: theme.slotHoverColor
            opacity = slot.hoverOverlayOpacity() ?: theme.slotHoverOpacity
            priority = STATE_PRIORITY_HOVER
        }
        val warnColor = slot.warnOverlayColor()
        if (priority < STATE_PRIORITY_WARN && warnColor != null) {
            color = warnColor
            opacity = 0.5f
            priority = STATE_PRIORITY_WARN
        }
        if (priority < 0 || (color ushr 24) == 0) {
            return null
        }
        return StateOverlay(color, opacity)
    }

    private fun drawStateRect(
        slot: Slot,
        box: Box,
        bridge: UiRenderBridge,
        color: Int,
        opacity: Float,
        cornerRadii: CornerRadii,
    ) {
        val geo = resolveItemGeometry(slot, box)
        val argb = packArgb(opacity, color)
        if (!cornerRadii.isZero()) {
            val fill = StyledFill.builder().cornerRadii(cornerRadii.toArray()).build()
            bridge.submitBatch(roundedBatch(geo.x, geo.y, geo.size, geo.size, argb, fill))
        } else {
            bridge.fillRect(geo.x, geo.y, geo.size, geo.size, argb)
        }
    }

    // ========== 选中轮廓 ==========

    private fun drawSelectedOverlay(
        slot: Slot,
        box: Box,
        bridge: UiRenderBridge,
        cornerRadii: CornerRadii,
        theme: ControlTheme,
    ) {
        if (!slot.selected()) {
            return
        }
        val overlayColor = slot.selectedOverlayColor()
        if (overlayColor != null && (overlayColor ushr 24) != 0) {
            val argb = packArgb(0.25f, overlayColor)
            if (!cornerRadii.isZero()) {
                drawRoundedBackground(box, bridge, argb, cornerRadii)
            } else {
                bridge.fillRect(box.x(), box.y(), box.width(), box.height(), argb)
            }
        }
        val outlineColor = slot.selectedOutlineColor() ?: theme.slotSelectedOutlineColor
        val outlineWidth = slot.selectedOutlineWidth() ?: theme.slotSelectedOutlineWidth
        if (outlineWidth > 0f) {
            drawBorder(box, bridge, outlineColor, outlineWidth, cornerRadii)
        }
    }

    private fun drawBorder(box: Box, bridge: UiRenderBridge, color: Int, width: Float, cornerRadii: CornerRadii) {
        if (!cornerRadii.isZero()) {
            val fill = StyledFill.builder()
                .cornerRadii(cornerRadii.toArray())
                .borderWidth(width)
                .borderColor(color)
                .build()
            bridge.submitBatch(roundedBatch(box.x(), box.y(), box.width(), box.height(), 0x00000000, fill))
            return
        }
        val x = box.x()
        val y = box.y()
        val w = box.width()
        val h = box.height()
        bridge.fillRect(x, y, w, width, color)
        bridge.fillRect(x, y + h - width, w, width, color)
        bridge.fillRect(x, y, width, h, color)
        bridge.fillRect(x + w - width, y, width, h, color)
    }

    private fun drawRoundedBackground(box: Box, bridge: UiRenderBridge, color: Int, radii: CornerRadii) {
        val fill = StyledFill.builder().cornerRadii(radii.toArray()).build()
        bridge.submitBatch(roundedBatch(box.x(), box.y(), box.width(), box.height(), color, fill))
    }

    // ========== 反馈动画 ==========

    private fun drawFeedback(
        slot: Slot,
        box: Box,
        bridge: UiRenderBridge,
        feedback: SlotFeedbackView,
        item: SlotItemView?,
        theme: ControlTheme,
    ) {
        val progress = feedback.progress.toFloat()
        when (feedback.kind) {
            FeedbackKind.SUCCESS -> {
                val color = slot.successColor() ?: theme.slotSuccessColor
                drawFullBoxOverlay(box, bridge, fadeAlpha(1.0 - progress, theme.slotSuccessMaxAlpha), color)
            }
            FeedbackKind.REJECTED -> {
                if (item != null) {
                    drawRejectedReplay(slot, box, bridge, item, progress, theme)
                }
                val failedColor = slot.failedColor() ?: theme.slotFailedColor
                drawBorder(box, bridge, failedColor, 1f, CornerRadii.ZERO)
            }
            FeedbackKind.FAILED -> {
                val color = slot.failedColor() ?: theme.slotFailedColor
                val raw = 1.0 - abs(progress - 0.5f) * 2.0
                drawFullBoxOverlay(box, bridge, fadeAlpha(raw, theme.slotFailedMaxAlpha), color)
            }
            FeedbackKind.TIMEOUT -> {
                val color = slot.timeoutColor() ?: theme.slotTimeoutColor
                drawFullBoxOverlay(box, bridge, fadeAlpha(1.0 - progress, theme.slotTimeoutMaxAlpha), color)
            }
        }
    }

    /** REJECTED：原物品淡显回放一遍 + 抖动偏移再回放一遍（替身效果）。 */
    private fun drawRejectedReplay(
        slot: Slot,
        box: Box,
        bridge: UiRenderBridge,
        item: SlotItemView,
        progress: Float,
        theme: ControlTheme,
    ) {
        val geo = resolveItemGeometry(slot, box)
        val alpha = theme.slotRejectedReplayAlpha
        drawReplayItem(bridge, item, geo.x, geo.y, geo.size, alpha)
        val shake =
            sin((progress * theme.slotRejectedShakeFrequency).toDouble()).toFloat() * theme.slotRejectedShakeAmplitude
        drawReplayItem(bridge, item, geo.x + shake, geo.y, geo.size, alpha)
    }

    private fun drawReplayItem(
        bridge: UiRenderBridge,
        item: SlotItemView,
        x: Float,
        y: Float,
        size: Float,
        alpha: Float,
    ) {
        bridge.pushAlpha(alpha)
        try {
            bridge.drawItemStack(
                item.itemId, item.nbt, item.count, false, true,
                "bottom-right", 0f, 0f, x, y, size, size,
            )
        } finally {
            bridge.popAlpha()
        }
    }

    // ========== ripple ==========

    /**
     * 内联 ripple 绘制逻辑，与 `EffectsCompositor.applyRippleIfActive` 等价。
     * 因 EffectsCompositor 为包级访问，此处在 layer 包内重新实现。
     */
    private fun applyRippleIfActive(slot: Slot, box: Box, bridge: UiRenderBridge) {
        val theme = ThemeAccess.current()
        for (state in RippleAnimator.active(slot)) {
            val color = state.color()
            val fadeRatio = state.alpha() / RippleAnimator.RIPPLE_PRESSED_ALPHA
            val resolvedColor = if ((color ushr 24) == 0) {
                StateLayer.withAlpha(theme.primary, theme.stateLayerPressedOpacity * fadeRatio)
            } else {
                StateLayer.withAlpha(color, fadeRatio)
            }
            val cornerRadii = (slot.cornerRadii() ?: slot.style().visual().cornerRadii()).toArray()
            bridge.applyRippleEffect(
                box.x(), box.y(), box.width(), box.height(),
                resolvedColor, state.progress(), state.radius(), state.centerX(), state.centerY(), cornerRadii,
            )
        }
    }

    // ========== 共用助手 ==========

    /** 物品区几何：图标边长 + 左上角绝对坐标（状态矩形与反馈回放共用）。 */
    private fun resolveItemGeometry(slot: Slot, box: Box): ItemGeometry {
        val size = slot.itemSize() ?: (slot.slotSize() - 2f)
        val insetX = slot.itemInsetX() ?: ((box.width() - size) / 2f)
        val insetY = slot.itemInsetY() ?: ((box.height() - size) / 2f)
        return ItemGeometry(size, box.x() + insetX, box.y() + insetY)
    }

    /** opacity(0..1) + RGB(低 24 位) 打包为 ARGB。 */
    private fun packArgb(opacity: Float, rgb: Int): Int = ((opacity * 255f).toInt() shl 24) or (rgb and 0x00FFFFFF)

    /** 钳制 raw 到 [0,1] 再乘最大透明度，得到淡入淡出的实际 alpha。 */
    private fun fadeAlpha(raw: Double, maxAlpha: Float): Float = raw.coerceIn(0.0, 1.0).toFloat() * maxAlpha

    /** 整框纯色叠加（反馈动画的 SUCCESS/FAILED/TIMEOUT 共用）。 */
    private fun drawFullBoxOverlay(box: Box, bridge: UiRenderBridge, alpha: Float, rgb: Int) {
        bridge.fillRect(box.x(), box.y(), box.width(), box.height(), packArgb(alpha, rgb))
    }

    private fun roundedBatch(x: Float, y: Float, w: Float, h: Float, color: Int, fill: StyledFill): RenderBatch =
        RenderBatch(
            RenderCommandType.ROUNDED_RECT,
            null,
            RenderCommand.NO_CLIP,
            listOf(RenderCommand.roundedRect(x, y, w, h, color, RenderCommand.NO_CLIP, fill)),
        )

    private data class ItemGeometry(val size: Float, val x: Float, val y: Float)

    private data class StateOverlay(val color: Int, val opacity: Float)

    private companion object {
        /** 状态叠加优先级（从高到低）：pressed > focus > disabled > hovered > warn。 */
        private const val STATE_PRIORITY_PRESSED = 4
        private const val STATE_PRIORITY_FOCUS = 3
        private const val STATE_PRIORITY_DISABLED = 2
        private const val STATE_PRIORITY_HOVER = 1
        private const val STATE_PRIORITY_WARN = 0
    }
}
